// App.js - Version 3.0.0 - FORCE NEW BUILD
import React, { useState, useEffect, useRef } from 'react';
import io from 'socket.io-client';
import EmojiPicker from 'emoji-picker-react';
import './App.css';
import './styles/SlackStyle.css';

// Use environment variables with fallbacks for development
const API_URL = process.env.REACT_APP_API_URL || 'https://chat.molnlagring.nu';
// For fetch calls we'll use relative URLs with the proxy
const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT || '';

// We'll build the verify session URL from API_URL to make it consistent
// This avoids hardcoding URLs that might differ from environment variables
const VERIFY_SESSION_URL = `${API_URL}/verify-session`;

// FORCE CODE CHANGE - EXTRA DEBUGGING
console.log('NEW APP VERSION 3.0 - FORCED REBUILD');
console.log('App initialized with:');
console.log('API_URL:', API_URL);
console.log('API_ENDPOINT:', API_ENDPOINT);
console.log('VERIFY_SESSION_URL:', VERIFY_SESSION_URL);
console.log('REACT_APP_SOCKET_URL:', process.env.REACT_APP_SOCKET_URL);
console.log('Browser localStorage:', localStorage);
console.log('Browser sessionStorage:', sessionStorage);
console.log('Current location:', window.location.href);

// Manual fix for hardcoded domains - runtime detection of correct domain
const RUNTIME_DOMAIN = window.location.hostname === 'chat.molnlagring.nu' ? 'chat.molnlagring.nu' : window.location.hostname;
console.log('Runtime domain detected:', RUNTIME_DOMAIN);

// Try to detect any cached URLs
const findCachedEndpoints = () => {
  const sources = [
    window.location.href,
    document.cookie,
    localStorage,
    sessionStorage,
    navigator.userAgent
  ];
  
  // Check multiple patterns
  const searchPatterns = [
    'api.molnlagring.nu', 
    'sl7w4am6w7.execute-api', 
    'r3uydpqs6i.execute-api'
  ];
  
  // Check each source against each pattern
  sources.forEach(source => {
    searchPatterns.forEach(pattern => {
      if (source && typeof source === 'string' && source.includes(pattern)) {
        console.warn(`Found problematic endpoint in string:`, pattern, source);
      } else if (source && typeof source === 'object') {
        Object.keys(source).forEach(key => {
          const value = source[key];
          if (value && typeof value === 'string' && value.includes(pattern)) {
            console.warn(`Found problematic endpoint in object:`, key, pattern, value);
          }
        });
      }
    });
  });
};

try {
  findCachedEndpoints();
} catch (e) {
  console.error('Error checking for cached endpoints:', e);
}

// Create a throttled fetch function to avoid hammering the API
const throttledFetch = (() => {
  const calls = {};
  
  return (url, options = {}, uniqueKey = url, throttleMs = 10000) => {
    const now = Date.now();
    const key = uniqueKey || url;
    
    // Check if we've called this URL recently
    if (calls[key] && (now - calls[key].timestamp) < throttleMs) {
      console.log(`[Throttle] Skipping call to ${url} - throttled (last call ${now - calls[key].timestamp}ms ago)`);
      return calls[key].promise;
    }
    
    // Make the actual fetch call
    console.log(`[Throttle] Making call to ${url}`);
    const promise = fetch(url, options);
    
    // Store this call
    calls[key] = {
      timestamp: now,
      promise
    };
    
    // Return the promise
    return promise;
  };
})();

const socket = io(process.env.REACT_APP_SOCKET_URL || API_URL, {
  withCredentials: true,
  transports: ['websocket', 'polling'],
  path: '/socket.io',
  reconnection: true,
  reconnectionAttempts: 5,
  reconnectionDelay: 1000,
  reconnectionDelayMax: 5000,
  timeout: 20000,
  autoConnect: false,
  forceNew: true,
  query: {
    timestamp: Date.now()
  }
});

// Add debug logging
socket.onAny((event, ...args) => {
  console.log(`[Socket Debug] Event: ${event}`, args);
});

socket.on('connect', () => {
  console.log('[Socket Debug] Connected with ID:', socket.id);
});

socket.on('connect_error', (err) => {
  console.error('[Socket Debug] Connection error:', err);
});

socket.on('disconnect', (reason) => {
  console.log('[Socket Debug] Disconnected:', reason);
});

// Function to store messages in localStorage
const storeMessages = (messagesArray) => {
  try {
    localStorage.setItem('notslackchat_messages', JSON.stringify(messagesArray));
    console.log('Messages saved to localStorage');
  } catch (err) {
    console.error('Error saving messages to localStorage:', err);
  }
};

// Function to retrieve messages from localStorage
const retrieveMessages = () => {
  try {
    const storedMessages = localStorage.getItem('notslackchat_messages');
    return storedMessages ? JSON.parse(storedMessages) : [];
  } catch (err) {
    console.error('Error retrieving messages from localStorage:', err);
    return [];
  }
};

function App() {
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState(() => retrieveMessages()); // Initialize from localStorage
  const [email, setEmail] = useState('');
  const [activeChannel, setActiveChannel] = useState('general');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginPassword, setLoginPassword] = useState('');
  const [error, setError] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profilePicture, setProfilePicture] = useState(null);
  const [userProfileImage, setUserProfileImage] = useState(null);
  const [tempUserData, setTempUserData] = useState(null);
  const messageInputRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const textareaRef = useRef(null);
  const messagesEndRef = useRef(null);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);

  // Simple emoji state
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [messageEmojiId, setMessageEmojiId] = useState(null);
  
  // Add state for image viewer
  const [imageViewer, setImageViewer] = useState({
    show: false,
    image: null,
    alt: ''
  });
  
  // Toggle main emoji picker
  const toggleEmojiPicker = () => {
    setShowEmojiPicker(!showEmojiPicker);
    setMessageEmojiId(null);
  };
  
  // Toggle message emoji picker
  const toggleMessageEmoji = (messageId) => {
    setMessageEmojiId(prev => prev === messageId ? null : messageId);
    setShowEmojiPicker(false);
  };
  
  // Handle emoji click for input
  const handleEmojiClick = (emojiData) => {
    if (messageInputRef.current) {
      const cursor = messageInputRef.current.selectionStart;
      const text = message.substring(0, cursor) + emojiData.emoji + message.substring(cursor);
      setMessage(text);
    }
    setShowEmojiPicker(false);
  };
  
  // Handle emoji reaction for messages
  const handleMessageEmoji = (emojiData, messageId) => {
    const updatedMessages = messages.map(msg => {
      if (msg.id === messageId) {
        const reactions = msg.reactions || [];
        return {
          ...msg,
          reactions: [...reactions, {
            emoji: emojiData.emoji,
            user: email,
            userName: name,
            userImage: userProfileImage,
            timestamp: new Date().toISOString()
          }]
        };
      }
      return msg;
    });
    
    setMessages(updatedMessages);
    storeMessages(updatedMessages);
    setMessageEmojiId(null);
    
    socket.emit('message reaction', {
      messageId,
      reaction: {
        emoji: emojiData.emoji,
        user: email,
        userName: name,
        userImage: userProfileImage
      },
      channel: activeChannel
    });
  };
  
  // Handle click outside to close emoji pickers
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(e.target)) {
        setShowEmojiPicker(false);
        setMessageEmojiId(null);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const createNewConversation = () => {
    const newConv = {
      id: Date.now().toString(),
      name: 'New Chat',
      messages: []
    };
    setConversations(prev => [...prev, newConv]);
    setSelectedConversation(newConv);
  };

  const selectConversation = (conversation) => {
    setSelectedConversation(conversation);
  };
  
  const selectUser = (user) => {
    console.log('Selecting user for DM:', user);
    setSelectedUser(user);
    setActiveChannel(`DM:${user.email}`);
    // Log the current messages that should be displayed
    const dmMessages = messages.filter(msg => 
      (msg.channel === `DM:${user.email}` && msg.sender === email) || 
      (msg.sender === user.email && msg.channel === `DM:${email}`)
    );
    console.log(`DM messages with ${user.name || user.email}:`, dmMessages);
  };

  useEffect(() => {
    // Check if user is already logged in by verifying the session
    const checkSession = async () => {
      try {
        // Construct a runtime URL that uses the current domain to avoid CORS issues
        const runtimeVerifySessionUrl = `https://${RUNTIME_DOMAIN}/verify-session`;
        console.log('Checking session...', runtimeVerifySessionUrl, 'at', new Date().toISOString());
        
        // Add error handling for CORS issues
        const response = await throttledFetch(runtimeVerifySessionUrl, {
          method: 'GET',
          credentials: 'include',
          mode: 'cors',
          headers: {
            'Accept': 'application/json',
            'Origin': `https://${RUNTIME_DOMAIN}`
          }
        }, 'session-verification').catch(err => {
          console.error('Fetch error:', err);
          // Return a fake response object to avoid breaking the code flow
          return {
            ok: false,
            status: 0,
            json: () => Promise.resolve({ message: 'Network error: CORS issue detected' })
          };
        });
        
        console.log('Session verification response:', response.status);
        if (response.ok) {
          const data = await response.json();
          console.log('Session verified, user data:', data);
          setEmail(data.email || '');
          setName(data.name || '');
          setIsLoggedIn(true);
          
          // Set user profile image if available
          if (data.picture) {
            setUserProfileImage(data.picture);
          }
          
          // Connect socket after successful session verification
          if (!socket.connected) {
            console.log('Connecting socket...');
            socket.connect();
          }
          socket.emit('user joined', data.email || data.id);
          
          // Fetch users list
          fetchUsers();
        } else {
          console.log('Session verification failed:', response.status);
          if (response.status === 0) {
            setError('Network error: Unable to connect to server due to CORS policy. Please contact administrator.');
          } else {
            const errorData = await response.json().catch(() => ({}));
            console.log('Error details:', errorData);
            setError(errorData.message || 'Failed to verify session');
          }
          setIsLoggedIn(false);
          setEmail('');
          setName('');
        }
      } catch (err) {
        console.error('Session verification error:', err);
        setError('Failed to connect to server. This might be due to CORS policy restrictions.');
        setIsLoggedIn(false);
        setEmail('');
        setName('');
      }
    };

    // Check session immediately
    checkSession();
    
    // Debug info - trace the origin of interval calls
    console.log('[Debug] Setting up checkSession interval:', new Error().stack);
    
    // Only poll for session if the user is NOT logged in
    // This prevents constant polling once the user is authenticated
    let interval;
    if (!isLoggedIn) {
      interval = setInterval(() => {
        console.log('[Debug] Running interval check for session at', new Date().toISOString());
        checkSession();
      }, 60000); // Reduced from 5 seconds to 60 seconds
    }

    // Socket event listeners
    socket.on('connect', () => {
      console.log('Socket connected successfully');
    });

    socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error.message);
      setError('Failed to connect to chat server. Please try refreshing the page.');
    });

    socket.on('disconnect', (reason) => {
      console.log('Socket disconnected:', reason);
      if (reason === 'io server disconnect') {
        // Server initiated disconnect, try to reconnect
        socket.connect();
      }
    });

    socket.on('chat message', (msg) => {
      console.log('Received chat message:', msg);
      setMessages(prev => {
        const newMessages = [...prev, msg];
        storeMessages(newMessages); // Store immediately on new message
        return newMessages;
      });
    });
    
    // Add listener for image messages
    socket.on('image message', (msg) => {
      console.log('Received image message:', msg);
      // Check if this is a new format message with s3Image or legacy format
      const messageToStore = msg.s3Image 
        ? msg  // New format with S3 URL
        : msg; // Legacy format with base64 data
      
      setMessages(prev => {
        const newMessages = [...prev, messageToStore];
        storeMessages(newMessages); // Store immediately on new image
        return newMessages;
      });
    });

    // Add listener for message deletion
    socket.on('message deleted', (data) => {
      console.log('Message deleted:', data.messageId);
      // Filter out the deleted message
      setMessages(prev => {
        const newMessages = prev.filter(msg => msg.id !== data.messageId);
        storeMessages(newMessages); // Store immediately on deletion
        return newMessages;
      });
    });

    // Add listener for message reactions
    socket.on('message reaction received', (data) => {
      console.log('Reaction received:', data);
      // Update the message with the new reaction
      setMessages(prev => prev.map(msg => {
        if (msg.id === data.messageId) {
          // Initialize reactions array if it doesn't exist
          const reactions = msg.reactions || [];
          
          // Add the new reaction
          return {
            ...msg,
            reactions: [...reactions, data.reaction]
          };
        }
        return msg;
      }));
    });

    // Socket event listeners for users
    socket.on('user list', (userList) => {
      console.log('Received user list:', userList);
      setUsers(userList);
    });
    
    socket.on('user joined', (userData) => {
      console.log('User joined:', userData);
      setUsers(prev => {
        if (!prev.some(user => user.email === userData.email)) {
          return [...prev, userData];
        }
        return prev;
      });
    });
    
    socket.on('user left', (userData) => {
      console.log('User left:', userData);
      // Update user status to offline or remove from list
      setUsers(prev => prev.map(user => 
        user.email === userData.email ? { ...user, status: 'offline' } : user
      ));
    });

    return () => {
      console.log('Cleaning up socket listeners');
      socket.off('connect');
      socket.off('connect_error');
      socket.off('disconnect');
      socket.off('chat message');
      socket.off('image message'); // Clean up the image message listener
      socket.off('message deleted'); // Clean up the deletion listener
      socket.off('message reaction received'); // Clean up the reaction listener
      if (socket.connected) {
        socket.disconnect();
      }
      if (interval) {
      clearInterval(interval);
      }
      socket.off('user list');
      socket.off('user joined');
      socket.off('user left');
    };
  }, []);

  // Add useEffect debug for users state
  useEffect(() => {
    console.log('Users state updated:', users);
    // Debug - force add current user to users list if not present
    if (isLoggedIn && email && users.length > 0) {
      if (!users.some(user => user.email === email)) {
        console.log('Adding current user to users list');
        setUsers(prev => [
          ...prev,
          {
            email,
            name,
            status: 'online',
            image_url: userProfileImage
          }
        ]);
      }
    }
  }, [users, isLoggedIn, email, name, userProfileImage]);

  // Add a useEffect to save messages to localStorage whenever they change
  useEffect(() => {
    if (messages.length > 0) {
      storeMessages(messages);
    }
  }, [messages]);

  // Enhanced fetchUsers to include debugging and error handling
  const fetchUsers = async () => {
    console.log('Fetching users list...');
    try {
      const response = await fetch(`${API_ENDPOINT}/listUsers`, {
        method: 'GET',
        credentials: 'include',
        mode: 'cors',
        headers: {
          'Accept': 'application/json',
          'Origin': `https://${RUNTIME_DOMAIN}`
        }
      });
      
      if (response.ok) {
        // Check if the response is valid JSON
        const text = await response.text();
        let data;
        
        try {
          // Try to parse the text as JSON
          data = JSON.parse(text);
        } catch (jsonError) {
          console.error('Error parsing users list response as JSON:', jsonError);
          console.log('Response text:', text);
          
          // If it's HTML or some other non-JSON response, we'll handle it differently
          if (text.includes('<!doctype') || text.includes('<html')) {
            console.warn('Received HTML response instead of JSON. This usually indicates a server-side error or redirect.');
            
            // If the current user is logged in, we'll add them to the users list anyway
            if (email) {
              setUsers([{
                email,
                name,
                status: 'online',
                image_url: userProfileImage
              }]);
            }
            return;
          }
          
          // Other non-JSON error
          throw new Error('Invalid JSON response from server');
        }
        
        console.log('User list fetched:', data);
        if (data.users && data.users.length > 0) {
          console.log('Setting users state with', data.users.length, 'users');
          
          // Make sure current user is part of the list with online status
          const usersList = data.users;
          const currentUserExists = usersList.some(user => user.email === email);
          
          if (!currentUserExists && email) {
            console.log('Adding current user to fetched users list');
            usersList.push({
              email,
              name,
              status: 'online',
              image_url: userProfileImage
            });
          }
          
          setUsers(usersList);
        } else {
          console.warn('No users returned from API or empty users array');
          
          // If API returns empty list, at least add current user
          if (email) {
            console.log('Creating users list with just current user');
            setUsers([{
              email,
              name,
              status: 'online',
              image_url: userProfileImage
            }]);
          }
        }
      } else {
        console.error('Failed to fetch users:', response.status);
        // Add current user to users list anyway if logged in
        if (email) {
          setUsers([{
            email,
            name,
            status: 'online',
            image_url: userProfileImage
          }]);
        }
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      // Add current user to users list anyway if logged in
      if (email) {
        setUsers([{
          email,
          name,
          status: 'online',
          image_url: userProfileImage
        }]);
      }
    }
  };

  const handleGoogleLogin = async () => {
    try {
      // Display user-friendly message
      setError('Redirecting to Google login...');
      
      // Google OAuth parameters
      const client_id = "399443935665-n70h6fi4qg7trq2nieoel0p7dodglse6.apps.googleusercontent.com";
      const redirect_uri = `https://${RUNTIME_DOMAIN}/auth/callback`;
      // Basic scopes only, no photo scopes
      const scope = "email profile openid";
      const response_type = "code";
      const access_type = "offline";
      
      // Generate state parameter
      const state = Math.random().toString(36).substring(2, 15);
      
      // Construct direct Google OAuth URL
      const directGoogleUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${client_id}&redirect_uri=${redirect_uri}&response_type=${response_type}&scope=${scope}&access_type=${access_type}&state=${state}&t=${Date.now()}`;
      
      console.log('Redirecting directly to Google:', directGoogleUrl);
      
      // Try both methods of redirection
      try {
        // Method 1: Using window.location.href
        window.location.href = directGoogleUrl;
        
        // Method 2: As fallback, open in new tab after small delay
        setTimeout(() => {
          window.open(directGoogleUrl, '_blank');
        }, 1000);
      } catch (redirectErr) {
        console.error('Redirect error:', redirectErr);
        setError('Redirect failed. Please try again.');
      }
    } catch (err) {
      setError('Failed to connect to Google');
      console.error('Google login error:', err);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    
    if (email.trim() && loginPassword.trim()) {
      try {
        const response = await fetch(`${API_ENDPOINT}/login`, {
          method: 'POST',
          credentials: 'include',
          mode: 'cors',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ email, password: loginPassword })
        }).catch(err => {
          console.error('Login fetch error:', err);
          throw new Error('Network error: CORS issue detected');
        });

        if (response.ok) {
          const data = await response.json();
          setEmail(data.email);
          setName(data.name);
          setIsLoggedIn(true);
          
          // Connect socket after successful login
          if (!socket.connected) {
            console.log('Connecting socket after login...');
            socket.connect();
          }
          socket.emit('user joined', data.email);
        } else {
          const errorData = await response.json().catch(() => ({ message: 'Login failed' }));
          setError(errorData.message || 'Login failed');
        }
      } catch (err) {
        console.error('Login error:', err);
        if (err.message.includes('Failed to fetch') || err.message.includes('CORS')) {
          setError('Network error: Unable to connect to server due to CORS policy. Please contact administrator.');
        } else {
          setError(err.message || 'Failed to connect to server');
        }
      }
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError('');
    
    if (loginPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      const response = await fetch(`${API_ENDPOINT}/addUser`, {
        method: 'POST',
        credentials: 'include',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ email, password: loginPassword, name })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create account');
      }

      // After successful signup, log the user in
      await handleLogin(e);
      
      // Show profile picture modal after successful login
      setShowProfileModal(true);
    } catch (err) {
      console.error('Signup error:', err);
      if (err.message.includes('Failed to fetch')) {
        setError('Network error: Please check your connection and try again.');
      } else {
        setError(err.message || 'Failed to create account. Please try again.');
      }
    }
  };

  const handleProfilePictureUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setProfilePicture(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCompleteProfile = async () => {
    try {
      console.log('Starting profile picture upload...');
      
      // Convert base64 to blob
      const fetchResponse = await fetch(profilePicture);
      const blob = await fetchResponse.blob();
      
      // Create FormData
      const formData = new FormData();
      formData.append('image', blob, 'profile.jpg');
      formData.append('email', email);
      
      const response = await fetch(`${API_ENDPOINT}/setImage`, {
        method: 'POST',
        credentials: 'include',
        mode: 'cors',
        body: formData
      });

      console.log('Profile picture upload response status:', response.status);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Profile picture upload error:', errorData);
        throw new Error(errorData.message || `Failed to update profile picture: ${response.status}`);
      }

      const data = await response.json();
      console.log('Profile update successful:', data);
      
      // Update the user's profile image in the state
      if (data.image_url) {
        setUserProfileImage(data.image_url);
      }
      
      // Close the modal
      setShowProfileModal(false);
      setProfilePicture(null);
      
      // Show success message
      setError(''); // Clear any existing errors
    } catch (err) {
      console.error('Profile update error:', err);
      if (err.message.includes('Failed to fetch')) {
        setError('Network error: Please check your connection and try again.');
      } else {
        setError(err.message || 'Failed to update profile picture. Please try again.');
      }
    }
  };

  const handleSkipProfile = async () => {
    try {
      console.log('Skipping profile picture...');
      
      // Create FormData for consistency
      const formData = new FormData();
      formData.append('email', email);
      formData.append('skip', 'true');
      
      const response = await fetch(`${API_ENDPOINT}/setImage`, {
        method: 'POST',
        credentials: 'include',
        mode: 'cors',
        body: formData
      });

      console.log('Skip profile response status:', response.status);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Skip profile error:', errorData);
        throw new Error(errorData.message || `Failed to skip profile picture: ${response.status}`);
      }

      // Close the modal
      setShowProfileModal(false);
      setProfilePicture(null);
      
      // Show success message
      setError(''); // Clear any existing errors
    } catch (err) {
      console.error('Skip profile error:', err);
      if (err.message.includes('Failed to fetch')) {
        setError('Network error: Please check your connection and try again.');
      } else {
        setError(err.message || 'Failed to skip profile picture. Please try again.');
      }
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_ENDPOINT}/logout`, {
        method: 'POST',
        credentials: 'include',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      
      // Clear the messages from localStorage when logging out for security
      localStorage.removeItem('notslackchat_messages');
      
      socket.disconnect();
      setIsLoggedIn(false);
      setEmail('');
      setName('');
      setMessages([]);
      setActiveChannel('general');
      setSelectedUser(null);
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  const handleSubmit = (e) => {
    if (e) e.preventDefault();
    if (message.trim()) {
      // Create the message object
      const newMsg = {
        text: message,
        sender: email,
        senderName: name,
        image_url: userProfileImage,
        timestamp: new Date().toISOString(),
        channel: activeChannel,
        id: Date.now().toString() // Adding unique ID for emoji reactions
      };
      
      // Emit to socket
      socket.emit('chat message', newMsg);
      
      // Add to local state immediately for better UX
      setMessages(prev => {
        const newMessages = [...prev, newMsg];
        storeMessages(newMessages); // Store immediately on send
        return newMessages;
      });
      
      // Clear input
      setMessage('');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Show loading indicator or temporary placeholder
      const tempId = Date.now().toString();
      const placeholderMessage = {
        id: tempId,
        sender: email,
        senderName: name,
        image_url: userProfileImage,
        timestamp: new Date().toISOString(),
        fileName: file.name,
        channel: activeChannel,
        isLoading: true,
        text: `Uploading ${file.name}...`
      };
      
      // Add placeholder to messages
      setMessages(prev => {
        const newMessages = [...prev, placeholderMessage];
        return newMessages;
      });
      
      try {
        // Read file as Data URL
        const reader = new FileReader();
        const dataUrl = await new Promise((resolve, reject) => {
          reader.onload = (event) => resolve(event.target.result);
          reader.onerror = (error) => reject(error);
          reader.readAsDataURL(file);
        });
        
        // Upload to S3 via Lambda
        const response = await fetch(`${API_ENDPOINT}/uploadChatImage`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Origin': `https://${RUNTIME_DOMAIN}`
          },
          credentials: 'include',
          body: JSON.stringify({
            image: dataUrl,
            fileName: file.name,
            sender: email
          })
        });
        
        if (!response.ok) {
          throw new Error('Failed to upload image');
        }
        
        const data = await response.json();
        
        // Remove placeholder and create real message with S3 URL
        const imageMessage = {
          s3Image: data.image_url, // Use S3 URL instead of base64 data
          sender: email,
          senderName: name,
          image_url: userProfileImage,
          timestamp: new Date().toISOString(),
          fileName: file.name,
          channel: activeChannel,
          id: tempId // Use same ID as placeholder to replace it
        };
        
        // Update messages
        setMessages(prev => {
          const newMessages = prev.map(msg => 
            msg.id === tempId ? imageMessage : msg
          );
          storeMessages(newMessages);
          return newMessages;
        });
        
        // Emit to socket with S3 URL instead of base64 data
        socket.emit('image message', imageMessage);
        
      } catch (error) {
        console.error('Error uploading image:', error);
        
        // Update placeholder with error
        setMessages(prev => {
          const newMessages = prev.map(msg => 
            msg.id === tempId ? {
              ...msg, 
              isLoading: false,
              isError: true,
              text: `Failed to upload ${file.name}: ${error.message}`
            } : msg
          );
          storeMessages(newMessages);
          return newMessages;
        });
      }
    }
  };

  const switchChannel = (channel) => {
    setActiveChannel(channel);
    setSelectedUser(null); // Clear selected user when switching to a channel
  };

  const handleDeleteMessage = (messageId) => {
    // Filter out the message with the given ID
    const updatedMessages = messages.filter(msg => msg.id !== messageId);
    setMessages(updatedMessages);
    storeMessages(updatedMessages); // Store immediately on delete
    
    // Emit to socket to notify other users about the deletion
    socket.emit('delete message', { messageId, channel: activeChannel });
  };

  // Add a specific function for direct messages
  const isDirectMessageVisible = (msg) => {
    if (!activeChannel.startsWith('DM:')) {
      // For regular channels, just check the channel name
      return msg.channel === activeChannel;
    }
    
    // For direct messages
    const dmTarget = activeChannel.replace('DM:', '');
    
    // Special case: self-chat (talking to yourself)
    if (dmTarget === email) {
      // For self-chat, show only messages where both sender and channel match the current user
      return msg.sender === email && (msg.channel === `DM:${email}` || msg.channel === activeChannel);
    }
    
    // Regular DM case:
    // 1. Current user sent to selected user (outgoing DM)
    const outgoingDM = msg.sender === email && msg.channel === activeChannel;
    
    // 2. Selected user sent to current user (incoming DM)
    const incomingDM = msg.sender === dmTarget && msg.channel === `DM:${email}`;
    
    console.log(`Message from ${msg.sender} to ${msg.channel}, visible: ${outgoingDM || incomingDM}`);
    
    return outgoingDM || incomingDM;
  };

  // Handle click on current user DM
  const handleSelfChat = () => {
    console.log('Clicked on self chat');
    setSelectedUser({
      email,
      name,
      status: 'online',
      image_url: userProfileImage
    });
    setActiveChannel(`DM:${email}`);
  };

  // Function to open image viewer
  const openImageViewer = (image, alt) => {
    setImageViewer({
      show: true,
      image,
      alt: alt || 'Image'
    });
  };
  
  // Function to close image viewer
  const closeImageViewer = () => {
    setImageViewer({
      show: false,
      image: null,
      alt: ''
    });
  };

  const styles = `
    .chat-container {
      max-width: 80%;
      margin: 0 auto;
      height: 100vh;
      display: flex;
      flex-direction: column;
    }

    .emoji-picker-container {
      position: absolute;
      bottom: 100%;
      right: 0;
      z-index: 1000;
      background: white;
      border: 1px solid #ddd;
      border-radius: 4px;
      box-shadow: 0 2px 10px rgba(0,0,0,0.1);
    }

    .selected-emoji {
      padding: 10px;
      border-top: 1px solid #ddd;
      display: flex;
      justify-content: space-between;
      align-items: center;
      background: #f8f9fa;
    }

    .selected-emoji span {
      font-size: 14px;
      color: #666;
    }

    .selected-emoji button {
      padding: 4px 8px;
      background: #007bff;
      color: white;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      font-size: 12px;
    }

    .selected-emoji button:hover {
      background: #0056b3;
    }
  `;

  if (!isLoggedIn) {
    return (
      <div className="login-container">
        <div className="login-box">
          <h2>Welcome to NotSlackChat</h2>
          {error && <div className="error-message">{error}</div>}
          
          <div className="auth-options">
            <button 
              className="google-login-button" 
              style={{
                display: 'block',
                textAlign: 'center',
                backgroundColor: '#DB4437', 
                fontWeight: 'bold',
                textDecoration: 'none',
                color: 'white',
                padding: '10px',
                width: '100%',
                border: 'none',
                cursor: 'pointer'
              }}
              onClick={handleGoogleLogin}
            >
              <i className="fab fa-google"></i> Continue with Google
            </button>
            </div>
                </div>
      </div>
    );
  }

  return (
    <div className="app-container">
      {/* Sidebar */}
      <div className="sidebar">
        <div className="workspace-header">
          <h2>NotSlackChat</h2>
          <div className="user-controls">
            <div 
              className="user-profile-image" 
              onClick={() => setShowProfileModal(true)}
              title="Update profile picture"
            >
              {userProfileImage ? (
                <img src={userProfileImage} alt={name} />
              ) : (
                <div className="profile-initial">{name[0]?.toUpperCase()}</div>
              )}
            </div>
          <button onClick={handleLogout} className="logout-button">
            <i className="fas fa-sign-out-alt"></i> Logout
          </button>
          </div>
        </div>
        
        <div className="sidebar-section">
          <h3>Channels</h3>
          <ul className="channel-list">
            <li 
              className={`channel ${activeChannel === 'general' ? 'active' : ''}`}
              onClick={() => switchChannel('general')}
            >
              <span className="channel-prefix">#</span> general
            </li>
            <li 
              className={`channel ${activeChannel === 'random' ? 'active' : ''}`}
              onClick={() => switchChannel('random')}
            >
              <span className="channel-prefix">#</span> random
            </li>
            <li 
              className={`channel ${activeChannel === 'help' ? 'active' : ''}`}
              onClick={() => switchChannel('help')}
            >
              <span className="channel-prefix">#</span> help
            </li>
          </ul>
        </div>
        
        <div className="sidebar-section">
          <h3>Direct Messages</h3>
          <ul className="dm-list">
            {/* Current user */}
            <li className="dm" onClick={handleSelfChat}>
              <span className="status-indicator online"></span> {name} (you)
            </li>
            
            {/* Other users */}
            {users.length > 0 ? (
              users.filter(user => user.email !== email).map((user, index) => {
                console.log('Rendering user in DM list:', user);
                return (
                  <li 
                    key={index} 
                    className={`dm ${selectedUser && selectedUser.email === user.email ? 'active' : ''}`}
                    onClick={() => {
                      console.log('Clicked on user:', user);
                      selectUser(user);
                    }}
                  >
                    <span className={`status-indicator ${user.status || 'offline'}`}></span>
                    <div className="dm-user-info">
                      <span className="dm-user-name">{user.name || user.email}</span>
                    </div>
                  </li>
                );
              })
            ) : (
            <li className="dm">
                <span className="status-indicator offline"></span>
                <div className="dm-user-info">
                  <span className="dm-user-name"></span>
                </div>
            </li>
            )}
          </ul>
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="main-content">
        {/* Channel Header */}
        <div className="channel-header">
          <div className="channel-info">
            {activeChannel.startsWith('DM:') ? (
              <h2>
                <span className="dm-prefix">
                  <span className={`status-indicator ${selectedUser?.status || 'offline'}`}></span>
                </span> 
                {selectedUser ? selectedUser.name || selectedUser.email : activeChannel.replace('DM:', '')}
              </h2>
            ) : (
            <h2><span className="channel-prefix">#</span> {activeChannel}</h2>
            )}
            <span className="channel-description">
              {activeChannel === 'general' ? 'Company-wide announcements' :
               activeChannel === 'random' ? 'Random discussions' :
               activeChannel === 'help' ? 'Get help and support' :
               activeChannel.startsWith('DM:') ? 'Direct message' : 'Chat'}
            </span>
          </div>
        </div>

        {/* Messages Container */}
        <div className="messages-container">
          {messages
            .filter(isDirectMessageVisible)
            .map((msg, index) => (
            <div key={index} className={`message ${msg.isLoading ? 'loading' : ''} ${msg.isError ? 'message-error' : ''}`}>
              <div className="message-avatar">
                  {msg.image_url ? (
                    <img src={msg.image_url} alt={msg.senderName} />
                  ) : (
                    msg.senderName[0].toUpperCase()
                  )}
              </div>
              <div className="message-content">
                <div className="message-header">
                  <span className="message-sender">{msg.senderName}</span>
                  <span className="message-timestamp">
                    {new Date(msg.timestamp).toLocaleTimeString()}
                  </span>
                  {msg.isLoading && (
                    <span className="message-loading-indicator">Uploading...</span>
                  )}
                </div>
                <div className="message-text-container">
                  <div className="message-text">
                    {msg.isLoading ? (
                      <>
                        <div>{msg.text}</div>
                        <div className="upload-progress">
                          <div className="upload-progress-bar" style={{ width: '60%' }}></div>
                        </div>
                      </>
                    ) : msg.s3Image ? (
                      <img 
                        src={msg.s3Image} 
                        alt={msg.fileName} 
                        onClick={() => openImageViewer(msg.s3Image, msg.fileName)}
                      />
                    ) : msg.image ? (
                      <img 
                        src={msg.image} 
                        alt={msg.fileName} 
                        onClick={() => openImageViewer(msg.image, msg.fileName)}
                      />
                    ) : (
                      msg.text
                    )}
                  </div>
                  <div className="message-hover-actions">
                    {/* Show delete option only if the current user is the sender */}
                    {msg.sender === email ? (
                      <button 
                        className="message-delete-button" 
                        onClick={() => handleDeleteMessage(msg.id)}
                        title="Delete message"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    ) : (
                      <button 
                        className="message-react-button" 
                        onClick={() => toggleMessageEmoji(msg.id)}
                        title="Add reaction"
                      >
                        😊
                      </button>
                    )}
                  </div>
                  {messageEmojiId === msg.id && (
                    <div className="message-emoji-picker-container">
                      <EmojiPicker onEmojiClick={(emoji) => handleMessageEmoji(emoji, msg.id)} />
                    </div>
                  )}
                  
                  {/* Display reactions if there are any */}
                  {msg.reactions && msg.reactions.length > 0 && (
                    <div className="message-reactions">
                      {msg.reactions.map((reaction, i) => (
                        <span 
                          key={i} 
                          className="message-reaction" 
                          title={reaction.userName}
                        >
                          {reaction.emoji}
                          <div className="reaction-user-tooltip">
                            <div className="reaction-user-image">
                              {reaction.userImage ? (
                                <img src={reaction.userImage} alt={reaction.userName} />
                              ) : (
                                <div className="reaction-user-initial">
                                  {reaction.userName?.[0]?.toUpperCase()}
                                </div>
                              )}
                            </div>
                            <div className="reaction-user-name">{reaction.userName}</div>
                          </div>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Message Input */}
        <div className="message-input-container">
          <button 
            type="button" 
            className="emoji-button" 
            onClick={toggleEmojiPicker}
          >
            😊
          </button>
          <input
            ref={messageInputRef}
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSubmit(e)}
            placeholder={activeChannel.startsWith('DM:') 
              ? `Message ${selectedUser?.name || selectedUser?.email || activeChannel.replace('DM:', '')}` 
              : `Message #${activeChannel}`}
            id="message-input"
          />
          <label htmlFor="file-input" className="action-button">
            <i className="fas fa-paperclip"></i>
          </label>
          <input
            type="file"
            id="file-input"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
          />
          <button type="button" onClick={handleSubmit} id="send-button">Send</button>
          {showEmojiPicker && (
            <div className="emoji-picker-container" ref={emojiPickerRef}>
              <EmojiPicker onEmojiClick={handleEmojiClick} />
            </div>
          )}
        </div>
      </div>

      {/* Image Viewer Modal */}
      {imageViewer.show && (
        <div className="modal-overlay" onClick={closeImageViewer}>
          <div className="image-viewer-modal" onClick={e => e.stopPropagation()}>
            <button className="close-button" onClick={closeImageViewer}>×</button>
            <img 
              src={imageViewer.image} 
              alt={imageViewer.alt} 
              className="expanded-image" 
            />
            <div className="image-caption">{imageViewer.alt}</div>
          </div>
        </div>
      )}

      {/* Profile Picture Modal */}
      {showProfileModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Update Profile Picture</h2>
            <p>Choose a profile picture to personalize your account</p>
            
            <div className="profile-picture-upload">
              <div className="profile-picture-preview">
                {profilePicture ? (
                  <img src={profilePicture} alt="Profile preview" />
                ) : userProfileImage ? (
                  <img src={userProfileImage} alt="Current profile" />
                ) : (
                  <div className="profile-picture-placeholder">
                    <i className="fas fa-user"></i>
                  </div>
                )}
              </div>
              
                  <label htmlFor="profile-picture-input" className="upload-button">
                {profilePicture ? 'Change Photo' : 'Choose Photo'}
                  </label>
                  <input
                    type="file"
                    id="profile-picture-input"
                    accept="image/*"
                    onChange={handleProfilePictureUpload}
                    style={{ display: 'none' }}
                  />
            </div>

            <div className="modal-actions">
              <button 
                onClick={handleCompleteProfile}
                className="primary-button"
                disabled={!profilePicture}
              >
                Save Profile Picture
              </button>
              <button 
                onClick={() => setShowProfileModal(false)}
                className="secondary-button"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App; 